import * as React from 'react';
import { FC, useCallback } from 'react';

import { NumberInput } from 'components/ucl/number-input';
import { useRedeemRewardState } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import {
  ICartEntryType,
  MAX_IN_RESTAURANT_CART_QUANTITY,
  MIN_IN_RESTAURANT_CART_QUANTITY,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';

import { displayValueStyleProps, incrementorStyleProps } from './styled';
import { IInRestaurantIncrementorProps } from './types';

const RewardRedemptionIncrementor: FC<React.PropsWithChildren<IInRestaurantIncrementorProps>> = ({
  cartEntry,
}) => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const {
    inRestaurantRedemptionCart,
    updateInRestaurantRedemptionEntryQuantity,
  } = useInRestaurantRedemptionContext();

  const { quantity, details } = cartEntry;

  const otherRewardQuantity = (inRestaurantRedemptionCart ?? [])
    .filter(entry => entry.type === ICartEntryType.REWARD && entry !== cartEntry)
    .reduce((sum, entry) => sum + (entry.quantity ?? 0), 0);

  const maxQuantity = enableRewardUnstacking
    ? Math.max(1, rewardRedemptionLimit - otherRewardQuantity)
    : MAX_IN_RESTAURANT_CART_QUANTITY;

  const {
    incentiveNotInMenu,
    limitPerOrderMet,
    hasSufficientBalance,
    redeemRewardLoading,
  } = useRedeemRewardState({
    reward: details.engineReward,
    quantity,
    cartId: details.engineReward.id,
  });

  const handleChange = useCallback(
    (value: number) => {
      updateInRestaurantRedemptionEntryQuantity(cartEntry, value);
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    },
    [cartEntry, updateInRestaurantRedemptionEntryQuantity]
  );

  // All hooks must be called before this early return to satisfy React rules.
  if (enableRewardUnstacking && rewardRedemptionLimit <= 1) {
    return null;
  }

  const disableIncrement =
    incentiveNotInMenu || limitPerOrderMet || !hasSufficientBalance || quantity >= maxQuantity;

  return (
    <NumberInput
      ariaLabel={`${quantity}`}
      min={MIN_IN_RESTAURANT_CART_QUANTITY}
      max={maxQuantity}
      value={quantity}
      onChange={handleChange}
      disableIncrement={disableIncrement}
      disabled={redeemRewardLoading}
      decrementAriaLabel={`Decrement ${quantity} quantity`}
      incrementAriaLabel={`Increment ${quantity} quantity`}
      displayValueStyleProps={displayValueStyleProps}
      incrementorStyleProps={incrementorStyleProps}
      hideNumberInput
      displayedValue={quantity}
    />
  );
};

export default RewardRedemptionIncrementor;
